/**
 * ResultsSummary Component
 *
 * Displays assessment results including:
 * - Overall readiness score
 * - Layer scores (Structural Readiness, Investment Leverage)
 * - Category breakdown
 * - Identified problems
 * - Recommended solutions
 * - Visual score indicators
 */

import React from "react";

import { SCORE_LEVELS } from "../constants";
import type {
  QuestionnaireLayer,
  QuestionnaireCategory,
  QuestionnaireQuestion,
  AssessmentResponse,
} from "../questionnaire.service";
import type { OverallScore } from "../utils/scoring.utils";
import { calculateOverallScore } from "../utils/scoring.utils";

interface ResultsSummaryProps {
  layers: QuestionnaireLayer[];
  categories: QuestionnaireCategory[];
  questions: QuestionnaireQuestion[];
  responses: AssessmentResponse[];
  onStartOver?: () => void;
  onDownloadReport?: () => void;
}

export const ResultsSummary: React.FC<ResultsSummaryProps> = ({
  layers,
  categories,
  questions,
  responses,
  onStartOver,
  onDownloadReport,
}) => {
  const results: OverallScore = calculateOverallScore(
    layers,
    categories,
    questions,
    responses,
  );
  const scoreLevel = SCORE_LEVELS[results.level];

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-4 py-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
          Assessment Complete!
        </h1>
        <p className="text-gray-600">
          Here's your AI Implementation Readiness Report
        </p>
      </div>

      {/* Overall Score Card */}
      <div
        className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl p-4 sm:p-8 mb-8 border-2"
        style={{ borderColor: scoreLevel.color }}
      >
        <div className="text-center">
          <div
            className="inline-flex items-center justify-center w-24 h-24 sm:w-32 sm:h-32 rounded-full mb-4"
            style={{ backgroundColor: `${scoreLevel.color}15` }}
          >
            <div
              className="text-4xl sm:text-5xl font-bold"
              style={{ color: scoreLevel.color }}
            >
              {Math.round(results.total_score)}
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {scoreLevel.label}
          </h2>
          <p className="text-lg text-gray-600 mb-6">{scoreLevel.description}</p>

          <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="text-sm text-gray-600 mb-1">
                Structural Readiness
              </div>
              <div className="text-2xl font-bold text-blue-600">
                {Math.round(results.structural_readiness_score)}
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="text-sm text-gray-600 mb-1">
                Investment Leverage
              </div>
              <div className="text-2xl font-bold text-purple-600">
                {Math.round(results.investment_leverage_score)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Layer Breakdown */}
      <div className="space-y-6 mb-8">
        {results.layers.map((layer) => (
          <div key={layer.layer_id} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                {layer.layer_name}
              </h3>
              <div className="text-2xl font-bold text-blue-600">
                {Math.round(layer.score)}
              </div>
            </div>

            {/* Category Scores */}
            <div className="space-y-3">
              {layer.categories.map((category) => (
                <div key={category.category_id}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">
                      {category.category_name}
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      {Math.round(category.score)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all"
                      style={{ width: `${category.score}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
        {onDownloadReport && (
          <button
            onClick={onDownloadReport}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Download Detailed Report
          </button>
        )}

        {onStartOver && (
          <button
            onClick={onStartOver}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
          >
            Start New Assessment
          </button>
        )}
      </div>

      {/* Next Steps */}
      <div className="mt-8 bg-blue-50 rounded-lg p-6 border border-blue-200">
        <h3 className="text-lg font-bold text-blue-900 mb-3">Next Steps</h3>
        <ul className="space-y-2 text-blue-800">
          <li className="flex items-start gap-2">
            <svg
              className="w-5 h-5 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>Review your detailed assessment report</span>
          </li>
          <li className="flex items-start gap-2">
            <svg
              className="w-5 h-5 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>Explore recommended solutions tailored to your needs</span>
          </li>
          <li className="flex items-start gap-2">
            <svg
              className="w-5 h-5 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>
              Schedule a consultation to discuss your AI implementation roadmap
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
};
